// Portfolio Administration v1.0 §7.4 — Required Insurance Policy Matrix.
// Renders a 5-card grid (one per required policy type from the
// RequiredInsurancePolicyCatalogItem seeds, INS-1..INS-5). Each card shows
// one of four states: In force / Expiring in N days (≤60d) / Lapsed /
// Not on file. Card click filters the policy table to that type.

import {
  RequirementMatrixCard,
  type RequirementIndicator,
  type RequirementStatusPill,
} from "../common/RequirementMatrixCard";
import { bandForDays, daysUntil } from "../common/colorBand";
import type { ColorBand } from "../common/colorBand";
import { isExpiringWithin, isLapsed } from "./policyStatus";
import type { InsurancePolicyDto } from "@/api/portfolioAdmin/types";

interface RequiredInsurancePolicyMatrixProps {
  policies: InsurancePolicyDto[];
  /** Card click → filter the policy table to this policyType (toggle). */
  onSelectType?: (policyType: string) => void;
}

interface RequiredSlot {
  code: string;
  /** Backend policyType value used for matching. */
  policyType: string;
  title: string;
  conditionHint: string;
}

/** Spec §7.4 — 5 required policy types per the INS-1..INS-5 catalog seeds. */
const REQUIRED_SLOTS: readonly RequiredSlot[] = [
  {
    code: "INS-1",
    policyType: "GeneralLiability",
    title: "General Liability",
    conditionHint: "Required for all loans. Phoenix + Seller as additional insured.",
  },
  {
    code: "INS-2",
    policyType: "Property",
    title: "Property",
    conditionHint: "Phoenix + Seller as loss payee.",
  },
  {
    code: "INS-3",
    policyType: "BusinessInterruption",
    title: "Business Interruption",
    conditionHint: "Phoenix + Seller as loss payee.",
  },
  {
    code: "INS-4",
    policyType: "KeyPersonLife",
    title: "Key Person Life",
    conditionHint: "Where key person named in BLA. Phoenix as loss payee.",
  },
  {
    code: "INS-5",
    policyType: "KeyPersonDisability",
    title: "Key Person Disability",
    conditionHint: "Where key person named in BLA. Phoenix as loss payee.",
  },
];

// `tz` is REQUIRED on both helpers below. The choice of WHICH policy fills a
// slot and the card drawn for it are two reads of one countdown; a default
// here would let the picker resolve a policy in Phoenix and the card describe
// it in the viewer's zone.
function pickPolicy(
  policies: InsurancePolicyDto[],
  policyType: string,
) {
  const matches = policies.filter((p) => p.policyType === policyType);
  if (matches.length === 0) return null;
  // Prefer in-force over lapsed; otherwise latest expiration.
  matches.sort((a, b) => {
    const aLapsed = isLapsed(a);
    const bLapsed = isLapsed(b);
    if (aLapsed !== bLapsed) return aLapsed ? 1 : -1;
    return b.expirationDate.localeCompare(a.expirationDate);
  });
  return matches[0];
}

function buildCard(policy: InsurancePolicyDto | null) {
  if (!policy) {
    return {
      indicators: [
        { label: "Coverage verified", status: "missing" as const },
        { label: "Renewal tracked", status: "missing" as const },
      ] satisfies RequirementIndicator[],
      statusPill: { kind: "missing", label: "Not on file" } as RequirementStatusPill,
      band: "red" as ColorBand,
    };
  }
  const days = daysUntil(policy.expirationDate);
  const lapsed = isLapsed(policy);
  const expiring = isExpiringWithin(policy, 60);
  const verified = !!policy.phoenixNamedAsVerifiedDate && !!policy.sellerNamedAsVerifiedDate;
  const indicators: RequirementIndicator[] = [
    {
      label: "Coverage verified (Phoenix + Seller)",
      status: verified ? "ok" : "missing",
    },
    {
      label: "Renewal tracked",
      status: lapsed ? "missing" : expiring ? "warn" : "ok",
    },
  ];
  let pill: RequirementStatusPill;
  let band: ColorBand;
  if (lapsed) {
    pill = { kind: "missing", label: "Lapsed" };
    band = "red";
  } else if (expiring) {
    pill = { kind: "warn", label: `Expiring in ${days} days` };
    band = days !== null && days <= 30 ? bandForDays(days) : "yellow";
  } else {
    pill = { kind: "ok", label: "In force" };
    band = "green";
  }
  return { indicators, statusPill: pill, band };
}

export function RequiredInsurancePolicyMatrix({
  policies,
  onSelectType,
}: RequiredInsurancePolicyMatrixProps) {
  const lapsed = REQUIRED_SLOTS.filter((s) => {
    const p = pickPolicy(policies, s.policyType);
    return !p || isLapsed(p);
  });
  return (
    <section
      className="flex flex-col gap-space-3"
      data-testid="insurance-required-matrix"
    >
      {lapsed.length > 0 && (
        <div
          className="rounded-md border-l-4 border-l-status-error bg-status-error/10 px-space-3 py-space-2"
          role="alert"
        >
          <p className="text-small text-onyx m-0">
            <strong>{lapsed.length}</strong> required{" "}
            {lapsed.length === 1 ? "policy is" : "policies are"} lapsed or
            not on file ({lapsed.map((s) => s.code).join(", ")}). A DefaultCase
            will auto-open in 30 days unless a renewal certificate is uploaded.
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-space-2">
        {REQUIRED_SLOTS.map((slot) => {
          const policy = pickPolicy(policies, slot.policyType);
          const { indicators, statusPill, band } = buildCard(policy);
          return (
            <RequirementMatrixCard
              key={slot.code}
              code={slot.code}
              title={slot.title}
              conditionHint={slot.conditionHint}
              indicators={indicators}
              statusPill={statusPill}
              band={band}
              onClick={
                onSelectType ? () => onSelectType(slot.policyType) : undefined
              }
              testId={`insurance-matrix-card-${slot.policyType}`}
            />
          );
        })}
      </div>
    </section>
  );
}
